<template>
  <dx-modal :value="show"
            :title="title"
            size="md"
            @input="close()"
            id="modal-cash-register-operation">
    <div class="stenci-group stenci-alert operation-help">
      <p>{{ help }}</p>
    </div>
    <div class="loading loading-lg" v-if="loading"></div>
    <div class="columns" v-else>
      <div class="column col-12 form-group" v-if="type !== 'expense'"
           :class="{'has-error': $v.form.bankAccountId.$error}">
        <label class="form-label">
          {{ type === 'supply' ? 'Conta de origem' : 'Conta de destino' }}
        </label>
        <select class="form-select" v-model="form.bankAccountId"
                @blur="$v.form.bankAccountId.$touch()">
          <option value="">[Selecione]</option>
          <option v-for="item in bankAccounts" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
        <template v-if="$v.form.bankAccountId.$error">
          <div class="form-input-hint">Campo obrigatório</div>
        </template>
      </div>
      <template v-else>
        <div class="column col-12 form-group" :class="{'has-error': $v.form.categoryId.$error}">
          <label class="form-label">Categoria</label>
          <select class="form-select" v-model="form.categoryId" @blur="$v.form.categoryId.$touch()">
            <option value="">[Selecione]</option>
            <option v-for="item in categories" :value="item.id" :key="item.id">
              <template v-if="item.parent">{{ item.parent.name }} > </template>{{ item.name }}
            </option>
          </select>
          <template v-if="$v.form.categoryId.$error">
            <div class="form-input-hint">Campo obrigatório</div>
          </template>
        </div>
        <div class="column col-12 form-group" :class="{'has-error': $v.form.entityName.$error}">
          <label class="form-label">Favorecido</label>
          <input type="text" class="form-input" v-model="form.entityName"
                 placeholder="Para quem foi pago" @blur="$v.form.entityName.$touch()">
          <template v-if="$v.form.entityName.$error">
            <div class="form-input-hint">Campo obrigatório</div>
          </template>
        </div>
      </template>
      <div class="column col-5 col-sm-12 form-group" :class="{'has-error': $v.form.value.$error}">
        <label class="form-label">Valor</label>
        <dx-input-number id="operation-value" v-model="form.value" class="form-input text-right"
                         :precision="2" maxlength="10" @blur="$v.form.value.$touch()"/>
        <template v-if="$v.form.value.$error">
          <div class="form-input-hint" v-if="!$v.form.value.minValue">Informe um valor</div>
          <div class="form-input-hint" v-else-if="!$v.form.value.maxValue">
            Maior que o dinheiro em caixa ({{ cashBalance | currency }})
          </div>
        </template>
      </div>
      <div class="column col-7 col-sm-12 form-group">
        <label class="form-label">Dinheiro em caixa</label>
        <input type="text" class="form-input text-right" readonly :value="cashBalance | currency">
      </div>
      <div class="column col-12 form-group">
        <label class="form-label">Observação</label>
        <textarea class="form-input" v-model="form.notes" rows="2"></textarea>
      </div>
    </div>
    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :class="{loading: saving}" :disabled="saving || loading" @click="save">
        <fa-icon :icon="['fal', 'check']"/>{{ confirmLabel }}
      </button>
      <button class="btn" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { required, minValue, maxValue } from 'vuelidate/lib/validators';

const TITLES = {
  supply: 'Suprimento de caixa',
  withdrawal: 'Sangria de caixa',
  expense: 'Despesa paga no caixa',
};

const LABELS = {
  supply: 'Registrar suprimento',
  withdrawal: 'Registrar sangria',
  expense: 'Registrar despesa',
};

const HELP = {
  supply: 'Entrada de dinheiro na gaveta vinda de outra conta, como o cofre ou a tesouraria. '
    + 'Use quando faltar troco ou precisar repor o caixa no meio do dia. '
    + 'O valor sai da conta de origem e entra no seu caixa.',
  withdrawal: 'Retirada de dinheiro da gaveta para outra conta, como o cofre, a tesouraria '
    + 'ou o banco. Use quando o caixa acumular mais espécie do que o necessário. '
    + 'O valor sai do seu caixa e entra na conta de destino.',
  expense: 'Pagamento feito com o dinheiro da gaveta, como um lanche ou uma compra pequena. '
    + 'O valor sai do seu caixa e vira uma despesa no financeiro, na categoria escolhida.',
};

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    cashBalance: {
      type: Number,
      default: 0,
    },
    sessionBankAccountId: {
      type: String,
      default: '',
    },
    defaultCategoryId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      bankAccounts: [],
      categories: [],
      form: {
        bankAccountId: '',
        categoryId: '',
        entityName: '',
        value: 0,
        notes: '',
      },
    };
  },
  computed: {
    title() {
      return TITLES[this.type];
    },
    confirmLabel() {
      return LABELS[this.type];
    },
    help() {
      return HELP[this.type];
    },
    takesCash() {
      return this.type !== 'supply';
    },
  },
  validations() {
    const rules = {
      form: {
        bankAccountId: {},
        categoryId: {},
        entityName: {},
        value: {
          required,
          minValue: minValue(0.01),
          maxValue: maxValue(this.takesCash ? this.cashBalance : Number.MAX_SAFE_INTEGER),
        },
      },
    };
    if (this.type === 'expense') {
      rules.form.categoryId = { required };
      rules.form.entityName = { required };
    } else {
      rules.form.bankAccountId = { required };
    }
    return rules;
  },
  mounted() {
    if (this.type === 'expense') {
      this.form.categoryId = this.defaultCategoryId || '';
      this.loadCategories();
    } else {
      this.loadBankAccounts();
    }
  },
  methods: {
    loadBankAccounts() {
      const params = { active: true, limit: 0 };
      this.$http.get('/bank-accounts', { params })
        .then(({ data }) => {
          this.bankAccounts = data.items
            .filter(({ id }) => id !== this.sessionBankAccountId);
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    loadCategories() {
      const params = { active: true, limit: 0 };
      this.$http.get('/categories', { params })
        .then(({ data }) => {
          const label = item => (item.parent ? `${item.parent.name} > ${item.name}` : item.name);
          this.categories = (data.items || [])
            .filter(({ type }) => type === 'expense')
            .sort((a, b) => label(a).localeCompare(label(b)));
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      const payload = { value: this.form.value, notes: this.form.notes || null };
      if (this.type === 'expense') {
        payload.categoryId = this.form.categoryId;
        payload.entityName = this.form.entityName;
      } else {
        payload.bankAccountId = this.form.bankAccountId;
      }

      this.saving = true;
      this.$http.post(`/cash-register/entries/${this.type}`, payload)
        .then(({ data }) => {
          this.$toast.success(`${this.title} registrado.`);
          this.$emit('saved', data);
          this.close(true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close(saved = false) {
      this.$emit('close', saved);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
#modal-cash-register-operation {
  .operation-help {
    font-size: $font-size-sm;
    margin-top: 0;
    p {
      margin: 0;
    }
  }
}
</style>
